import { messaging } from '../firebase';
import { getToken, onMessage, MessagePayload } from 'firebase/messaging';
import { apiPost } from '../api';

const VAPID_KEY = process.env.REACT_APP_VAPID_KEY;

export const requestNotificationPermission = async (): Promise<string | null> => {
    if (!('Notification' in window)) {
        console.error('This browser does not support desktop notification');
        return null;
    }

    try {
        const permission = await Notification.requestPermission();
        if (permission === 'granted') {
            // Get the FCM token
            if (messaging) {
                try {
                    // Register the service worker manually since the central one is disabled
                    await navigator.serviceWorker.register('/firebase-messaging-sw.js');
                    const registration = await navigator.serviceWorker.ready;

                    const token = await getToken(messaging, {
                        vapidKey: VAPID_KEY,
                        serviceWorkerRegistration: registration
                    });

                    if (token) {
                        await syncTokenToBackend(token);
                        return token;
                    }
                } catch (swError) {
                    console.error('Service Worker registration failed: ', swError);
                }
            }
        } else {
            // Permission denied
        }
    } catch (error) {
        console.error('An error occurred while retrieving token. ', error);
    }
    return null;
};

// Singleton unsubscribe — ensures only ONE onMessage listener is ever active at a time
let _activeUnsubscribe: (() => void) | null = null;

export const setupMessageListener = (callback: (payload: MessagePayload) => void) => {
    if (!messaging) return;
    // Tear down any existing listener before registering a new one
    if (_activeUnsubscribe) {
        _activeUnsubscribe();
        _activeUnsubscribe = null;
    }
    const unsubscribe = onMessage(messaging, callback);
    _activeUnsubscribe = unsubscribe;
    return () => {
        if (_activeUnsubscribe === unsubscribe) {
            _activeUnsubscribe = null;
        }
        unsubscribe();
    };
};

export const syncTokenToBackend = async (fcmToken: string): Promise<void> => {
    try {
        await apiPost('/api/notifications/register-device', { fcm_token: fcmToken });
        // Token synced
    } catch (error) {
        console.error('Failed to sync FCM Token to backend:', error);
    }
};

export const unregisterToken = async (fcmToken: string): Promise<void> => {
    try {
        await apiPost('/api/notifications/unregister-device', { fcm_token: fcmToken });
        // Token unregistered
    } catch (error) {
        console.error('Failed to unregister FCM Token:', error);
    }
};
